import React, { ReactNode, useState } from "react";
import styled, { css } from "styled-components";
import { AiOutlineSearch } from "react-icons/ai";

import Label from "../../style-guide/Inputs/Label";
import Section from "../../style-guide/Section/Section";
import TextInput from "../../style-guide/Inputs/TextInput";

interface WrapperProps {
  dontCompensate?: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  ${(props) =>
    !props.dontCompensate &&
    css`
      margin-top: -${(props) => props.theme.spacing.xs};
    `};
`;

const Nodes = styled.div``;

const Node = styled.div``;

interface ItemProps {
  level: number;
}

const Item = styled.div<ItemProps>`
  display: flex;
  flex-direction: row;
  border: 1px solid ${(props) => props.theme.colors.borderDim};
  padding: ${(props) => props.theme.spacing.xs};
  align-items: center;
  padding-left: ${(props) =>
    `calc(${props.theme.spacing.small} * ${props.level})`};

  &:not(:last-child) {
    border-bottom: none;
  }
`;

const Check = styled.span`
  display: inline-block;
  padding-right: ${(props) => props.theme.spacing.xs};
`;

const Actions = styled.div`
  margin-left: auto;
`;

export interface Node {
  _id: string;
  [key: string]: any;
}

interface Props {
  tree: Node[];
  labelField: string;
  childrenField: string;
  values?: string[];
  dontCompensate?: boolean;
  onChange?: (id: string, selected: boolean, currentPath: Node[]) => any;
  renderActions?: (node: Node) => ReactNode;
  readOnly?: boolean;
  radio?: boolean;
  rootLabel?: string;
  enableSeach?: boolean;
}

export const Tree: React.FC<Props> = ({
  tree,
  labelField,
  childrenField,
  values,
  onChange,
  renderActions,
  readOnly,
  dontCompensate,
  radio,
  rootLabel,
  enableSeach
}) => {
  const [query, setQuery] = useState("");

  return (
    <Wrapper dontCompensate={dontCompensate}>
      {enableSeach && (
        <Section tight>
          <TextInput
            icon={<AiOutlineSearch />}
            placeholder="Søk etter innhold"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Section>
      )}
      <Nodes>
        {tree.map((n) =>
          renderNode({
            node: n,
            level: 1,
            labelField,
            childrenField,
            values,
            onChange,
            renderActions,
            readOnly,
            radio,
            rootLabel,
            currentPath: [],
            query
          })
        )}
      </Nodes>
    </Wrapper>
  );
};

interface RenderNodeOpts {
  node: Node;
  level: number;
  labelField: string;
  childrenField: string;
  currentPath: Node[];
  values?: string[];
  onChange?: (id: string, selected: boolean, currentPath: Node[]) => any;
  renderActions?: (node: Node) => ReactNode;
  readOnly?: boolean;
  radio?: boolean;
  rootLabel?: string;
  query: string;
}

function renderNode(opts: RenderNodeOpts) {
  const label = getLabel(opts.node, opts.labelField, opts.rootLabel);
  const children: Node[] = Array.isArray(opts.node[opts.childrenField])
    ? opts.node[opts.childrenField]
    : [];

  if (opts.query && !matchesQuery(opts.query, opts)) {
    return null;
  }

  return (
    <Node key={opts.node._id}>
      <Item level={opts.level}>
        <div>
          {typeof opts.onChange === "function" && (
            <Check>
              <input
                id={"tree_" + opts.node._id}
                type={opts.radio ? "radio" : "checkbox"}
                onChange={(e) =>
                  opts.onChange!(
                    opts.node._id,
                    e.target.checked,
                    opts.currentPath
                  )
                }
                disabled={opts.readOnly}
                checked={
                  Array.isArray(opts.values)
                    ? opts.values.includes(opts.node._id)
                    : false
                }
              />
            </Check>
          )}
          <Label htmlFor={"tree_" + opts.node._id} inline>
            {label}
          </Label>
        </div>
        {typeof opts.renderActions === "function" && (
          <Actions>{opts.renderActions(opts.node)}</Actions>
        )}
      </Item>
      {children.length > 0 && (
        <Nodes>
          {opts.node[opts.childrenField].map((n: Node) =>
            renderNode({
              ...opts,
              node: n,
              level: opts.level + 1,
              currentPath: [...opts.currentPath, opts.node]
            })
          )}
        </Nodes>
      )}
    </Node>
  );
}

function matchesQuery(query: string, opts: RenderNodeOpts): boolean {
  if (!query) {
    return true;
  }

  const lowerQuery = query.toLowerCase();
  const label = getLabel(opts.node, opts.labelField, opts.rootLabel);
  const children: Node[] = Array.isArray(opts.node[opts.childrenField])
    ? opts.node[opts.childrenField]
    : [];

  if (label.toLowerCase().includes(lowerQuery)) {
    return true;
  }

  return children.some((child) =>
    matchesQuery(query, { ...opts, node: child })
  );
}

function getLabel(node: Node, labelField: string, rootLabel?: string): string {
  let label: string = node[labelField];

  if (rootLabel && label === "__root") {
    label = rootLabel;
  }

  return label;
}
